import { useState } from "react";
import type { ActivitySettings } from "@/mocks/adminData";

interface ActivitySectionProps {
  settings: ActivitySettings;
}

const DEFAULT_RIGHT_IMAGE = "https://www.au2p0rz.vip/assets/activity-person-pc-CwU3lVY0.webp";

// 渲染一段文字：支持 **红色**、[链接文字](url)、数字自动红色
function renderLine(line: string) {
  // 先按 [文字](url) 和 **文字** 分割
  const parts = line.split(/(\[([^\]]+)\]\(([^)]+)\)|\*\*[^*]+\*\*)/g);
  const result: React.ReactNode[] = [];
  let i = 0;

  while (i < parts.length) {
    const part = parts[i];
    if (!part) { i++; continue; }

    // [文字](url) 超链接
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      result.push(
        <a
          key={i}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          className="text-red-500 font-semibold underline underline-offset-2 hover:text-red-600 cursor-pointer"
        >
          {linkMatch[1]}
        </a>
      );
      i++;
      continue;
    }

    // **红色** 文字
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      result.push(
        <span key={i} className="text-red-500 font-semibold">
          {part.slice(2, -2)}
        </span>
      );
      i++;
      continue;
    }

    // 普通文字：数字+金额单位自动红色
    const numParts = part.split(/(\d+[\$美元万]?)/g);
    result.push(
      <span key={i}>
        {numParts.map((np, j) => {
          if (/^\d+[\$美元万]?$/.test(np) && np.length > 0) {
            return <span key={j} className="text-red-500 font-semibold">{np}</span>;
          }
          return <span key={j}>{np}</span>;
        })}
      </span>
    );
    i++;
  }

  return result;
}

export default function ActivitySection({ settings }: ActivitySectionProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyContact = (e: React.MouseEvent) => {
    e.stopPropagation();
    const contact = settings.contact || "@hx_zs888";
    navigator.clipboard.writeText(contact).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSectionClick = () => {
    const url = settings.redirect_url?.trim();
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const hasRedirect = Boolean(settings.redirect_url?.trim());
  const rightImage = settings.right_image?.trim() || DEFAULT_RIGHT_IMAGE;

  return (
    <section id="activity" className="w-full py-5 md:py-10" style={{ background: "#eef2f7" }}>
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* 标题 */}
        <div className="flex items-center gap-2 mb-4 md:mb-6">
          <span className="w-1 h-5 rounded-full bg-red-500 flex-shrink-0"></span>
          <h2 className="text-base md:text-xl font-bold text-gray-800">活动专栏</h2>
          {hasRedirect && (
            <span className="flex items-center gap-1 text-xs text-red-400 font-medium">
              <i className="ri-external-link-line"></i>点击查看详情
            </span>
          )}
        </div>

        <div
          className={`rounded-xl overflow-hidden${hasRedirect ? " cursor-pointer transition-opacity hover:opacity-95 active:opacity-90" : ""}`}
          style={{
            backgroundImage: "url('https://storage.readdy-site.link/project_files/954c79ba-0cb1-4304-85c8-3e9b7d269137/bb188a4d-355a-41c3-90e3-4cbe13af2e9a_activity-bg-h5-BJy7dVpi.webp?v=9b1c23c73bf693ef72a93dfb528750b8')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          onClick={hasRedirect ? handleSectionClick : undefined}
          role={hasRedirect ? "link" : undefined}
          tabIndex={hasRedirect ? 0 : undefined}
          onKeyDown={hasRedirect ? (e) => { if (e.key === "Enter") handleSectionClick(); } : undefined}
        >
          <div className="flex flex-col lg:flex-row">
            {/* Left Content */}
            <div className="flex-1 p-5 md:p-8">
              <h3 className="text-base md:text-lg font-bold text-gray-900 mb-4 md:mb-5">合作奖励</h3>

              {settings.items.map((item) => (
                <div key={item.id} className="mb-4 md:mb-5">
                  {/* 活动标题：支持超链接 */}
                  <p className="text-base md:text-lg font-bold text-gray-900 mb-2">
                    {item.title_url?.trim() ? (
                      <a
                        href={item.title_url.trim()}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="text-gray-900 hover:text-red-500 transition-colors underline underline-offset-2 cursor-pointer"
                      >
                        {item.title}
                        <i className="ri-external-link-line text-xs ml-1 text-red-400"></i>
                      </a>
                    ) : (
                      item.title
                    )}
                  </p>
                  <div className="space-y-1.5">
                    {item.lines.map((line, idx) => (
                      <p key={idx} className="text-sm md:text-base font-medium text-gray-800">
                        {renderLine(line)}
                      </p>
                    ))}
                  </div>
                </div>
              ))}

              {settings.footer_note && (
                <p className="text-sm font-medium text-gray-600 mb-4">{settings.footer_note}</p>
              )}

              <div className="flex items-center gap-2">
                <span className="text-base text-gray-900 font-semibold">商务合作：</span>
                <button
                  onClick={handleCopyContact}
                  className="text-sm font-bold cursor-pointer transition-colors whitespace-nowrap"
                  style={{ color: "#6c3fc5" }}
                  title="点击复制"
                >
                  {copied ? "已复制!" : settings.contact}
                </button>
              </div>
            </div>

            {/* Right Image */}
            <div className="lg:w-72 xl:w-80 flex items-center justify-center p-4 md:p-6 border-t lg:border-t-0 lg:border-l border-white/10">
              <img
                src={rightImage}
                alt="活动商务负责人"
                className="w-full max-w-xs object-contain rounded-lg"
                loading="lazy"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
